import React, { Suspense, useMemo, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  DigitalTwinShell,
  HealthRadarCard,
  MetricGrid,
  RankBar,
  RiskBar,
  WeightChangeCard,
  BodyModelViewer,
  YearSlider,
  rankSegOf,
  riskColorOf,
} from './DigitalTwinShared';
import { DiseaseApiKey, buildVitalMetrics, useDigitalTwin } from './digitalTwinApi';

// 연도 대신 시점 라벨 — 0:3개월전, 1:현재, 2:5년 후, 3:10년 후
const YEAR_STOPS = [0, 1, 2, 3];
const YEAR_LABELS = ['3개월전', '현재', '5년 후', '10년 후'];
const CURRENT_YEAR = 1; // '현재'

/**
 * 화면 5: 나의 변화 분석 (상세)
 *  - 화면 4의 "자세히 분석" 버튼에서 ?year= 쿼리로 진입
 *  - 진입 시 화면 4에서 드래그한 연도/비만 변화가 그대로 이어짐
 */
const DigitalTwinScreen5 = () => {
  const [searchParams] = useSearchParams();

  // 화면 4에서 넘어온 시점 값 (0~3, 연속값 허용)
  const initialYear = (() => {
    const y = parseFloat(searchParams.get('year') ?? '');
    return Number.isFinite(y) ? Math.min(3, Math.max(0, y)) : CURRENT_YEAR;
  })();

  const [year, setYear] = useState<number>(initialYear);

  // 화면 1 과 같은 AP_009_002 결과를 모듈 캐시로 공유한다 (중복 호출 없음)
  const { data: twin } = useDigitalTwin();
  const vitals = useMemo(() => buildVitalMetrics(twin?.user?.healthIndicators), [twin]);

  // 시점 → 비만 morph (현재:0 ~ 10년 후:1)
  const obesityLevel = Math.max(0, Math.min(1, (year - CURRENT_YEAR) / 2));

  /*
   * 위험도 = AP_009_002 의 해당 질환 score(0~100).
   * 어떤 질환인지는 화면 4가 ?disease= 로 넘겨준다. 없으면 4종 중 가장 높은
   * 점수를 쓴다 — 화면 5로 직접 들어온 경우 아무 값이나 고르는 것보다 낫다.
   */
  const riskValue = useMemo(() => {
    const key = searchParams.get('disease') as DiseaseApiKey | null;
    const all = twin?.disease;
    if (!all) return 0;
    if (key && all[key]) return Math.round(all[key].score ?? 0);
    const scores = Object.values(all).map((d) => d?.score ?? 0);
    return scores.length ? Math.round(Math.max(...scores)) : 0;
  }, [twin, searchParams]);

  // '나의 등수'는 AP_009_002 에 대응 필드가 없다 (Notion APP-DT 스펙 확인).
  // 서버가 내려주기 전까지는 표시하지 않는다 — 임의의 숫자를 등수처럼 보여주지 않는다.
  const rankValue: number | null = null;
  const riskColor = riskColorOf(riskValue);
  const rankColor = rankValue === null ? '#777' : rankSegOf(rankValue).color;

  return (
    <DigitalTwinShell>
      {/* 타이틀 */}
      <Typography sx={{ mt: 2.5, textAlign: 'center', color: '#2A4268', lineHeight: 1.2 }}>
        <Box component="span" sx={{ fontWeight: 400, fontSize: 19 }}>나의 </Box>
        <Box component="span" sx={{ fontWeight: 800, fontSize: 25 }}>변화 분석</Box>
      </Typography>

      {/* 3D 모델 (확대, 화면 1과 동일 채도/밝기, 표정 neutral) */}
      <Box
        sx={{
          position: 'relative',
          mx: 1,
          mt: 1,
          height: 440,
          filter: 'saturate(0.9) brightness(1.04)',
        }}
      >
        <Suspense
          fallback={
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: '#8aa2ad' }} />
            </Box>
          }
        >
          <BodyModelViewer expression="neutral" obesityLevel={obesityLevel} />
        </Suspense>
      </Box>

      {/* 시점 슬라이더 */}
      <Box sx={{ mx: 3, mt: 1.5 }}>
        <YearSlider years={YEAR_STOPS} labels={YEAR_LABELS} value={year} onChange={setYear} />
      </Box>

      {/* 6개 핵심 지표 */}
      <Box sx={{ mx: 2, mt: 3 }}>
        <MetricGrid metrics={vitals} />
      </Box>

      {/* 위험도 */}
      <Box sx={{ mx: 2, mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ color: '#333', fontSize: 14, fontWeight: 700 }}>위험도</Typography>
          <Typography sx={{ color: riskColor, fontSize: 22, fontWeight: 900 }}>
            {riskValue} <Box component="span" sx={{ fontSize: 12, color: '#777' }}>%</Box>
          </Typography>
        </Stack>
        <Box sx={{ bgcolor: '#fff', borderRadius: '14px', p: 2, boxShadow: '0 2px 8px rgba(197,211,218,.4)' }}>
          <RiskBar value={riskValue} />
        </Box>
      </Box>

      {/* 나의 등수 — 서버가 등수를 내려주기 시작하면 rankValue 만 채우면 된다 */}
      {rankValue !== null && (
        <Box sx={{ mx: 2, mt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ color: '#333', fontSize: 14, fontWeight: 700 }}>나의 등수</Typography>
            <Typography sx={{ color: rankColor, fontSize: 22, fontWeight: 900 }}>
              {rankValue} <Box component="span" sx={{ fontSize: 12, color: '#777' }}>등</Box>
            </Typography>
          </Stack>
          <Box sx={{ bgcolor: '#fff', borderRadius: '14px', p: 2, boxShadow: '0 2px 8px rgba(197,211,218,.4)' }}>
            <RankBar value={rankValue} />
          </Box>
        </Box>
      )}

      {/* 최근 체중 변화 */}
      <WeightChangeCard />

      {/* 5각 레이더 차트 */}
      <HealthRadarCard />
    </DigitalTwinShell>
  );
};

export default DigitalTwinScreen5;
